import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Clock, Users, PlayCircle, Award, Check } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { MOCK_COURSES } from '../utils/mockData';

function CourseDetailPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);

  useEffect(() => {
    fetchCourse();
  }, [courseId]);

  const fetchCourse = async () => {
    // For this frontend-only demo, use mock data instead of a backend API
    const numericId = Number(courseId);
    const found = MOCK_COURSES.find((c) => c.id === numericId);

    if (!found) {
      toast.error('Course not found');
      navigate('/courses');
      return setLoading(false);
    }

    setCourse(found);
    setLoading(false);
  };

  const handlePurchase = async () => {
    // Demo-only behaviour: no real checkout backend wired
    toast.info('Checkout is not connected to a backend in this demo.');
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading course...</p>
        </div>
      </div>
    );
  }

  if (!course) return null;

  return (
    <div className="min-h-screen pt-24" data-testid="course-detail-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-4">
                <span className="badge bg-blue-600 text-white">{course.level}</span>
                {course.students_count > 50 && <span className="badge bg-amber-500 text-white">Best Seller</span>}
              </div>
              <h1 className="text-4xl sm:text-5xl font-extrabold mb-4" data-testid="course-title">{course.title}</h1>
              <p className="text-xl text-slate-300 mb-6">{course.description}</p>
              <div className="flex flex-wrap items-center gap-6 text-slate-300">
                {course.rating > 0 && (
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${i < Math.floor(course.rating) ? 'fill-amber-400 text-amber-400' : 'text-slate-600'}`}
                        />
                      ))}
                    </div>
                    <span className="font-semibold">{course.rating}</span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  <span>{course.students_count} students</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{course.lessons ? course.lessons.length : 0} lessons</span>
                </div>
              </div>
            </div>

            {/* Purchase Card */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl p-6 shadow-2xl text-slate-900 sticky top-24">
                {course.preview_video && (
                  <div className="mb-4">
                    <div className="aspect-video bg-slate-900 rounded-xl overflow-hidden">
                      <iframe
                        src={course.preview_video}
                        className="w-full h-full"
                        allowFullScreen
                        title="Course preview"
                      />
                    </div>
                  </div>
                )}
                <div className="text-4xl font-extrabold text-blue-600 mb-4" data-testid="course-price">${course.price}</div>
                <button
                  onClick={handlePurchase}
                  disabled={purchasing}
                  className="w-full btn-primary mb-4"
                  data-testid="purchase-button"
                >
                  {purchasing ? 'Processing...' : 'Buy Now'}
                </button>
                <div className="space-y-3 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>Lifetime access</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>Certificate of completion</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span>30-day money-back guarantee</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-12">
            {/* What You'll Learn */}
            <section>
              <h2 className="text-3xl font-extrabold text-slate-900 mb-6">What You'll Learn</h2>
              <div className="bg-stone-50 rounded-2xl p-8 border border-stone-100">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Master core concepts and fundamentals',
                    'Build real-world projects',
                    'Best practices and industry standards',
                    'Advanced techniques and strategies',
                    'Problem-solving skills',
                    'Portfolio-ready projects',
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Course Content */}
            <section>
              <h2 className="text-3xl font-extrabold text-slate-900 mb-6">Course Content</h2>
              <div className="space-y-3">
                {course.lessons && course.lessons.map((lesson, index) => {
                  // Extract values to avoid complex member expressions
                  const lessonIndex = index + 1;
                  const lessonTitle = lesson.title;
                  const lessonDescription = lesson.description;
                  const lessonDuration = lesson.duration;
                  const lessonId = lesson.id;
                  const animationDelay = index * 0.05;

                  return (
                    <motion.div
                      key={lessonId}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: animationDelay }}
                      className="bg-white rounded-xl p-6 border border-slate-100 hover:shadow-lg transition-shadow"
                      data-testid={`lesson-${lessonId}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <PlayCircle className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900 mb-1">
                              {lessonIndex}. {lessonTitle}
                            </h3>
                            <p className="text-sm text-slate-600">{lessonDescription}</p>
                          </div>
                        </div>
                        <div className="text-sm text-slate-600 whitespace-nowrap ml-4">{lessonDuration} min</div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>

            {/* Reviews */}
            {course.reviews && course.reviews.length > 0 && (
              <section>
                <h2 className="text-3xl font-extrabold text-slate-900 mb-6">Student Reviews</h2>
                <div className="space-y-4">
                  {course.reviews.map((review) => {
                    // Extract review data to avoid complex expressions
                    const reviewId = review.id;
                    const userName = review.user_name;
                    const userInitial = userName.charAt(0).toUpperCase();
                    const rating = review.rating;
                    const comment = review.comment;

                    return (
                      <div key={reviewId} className="bg-white rounded-xl p-6 border border-slate-100">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                            {userInitial}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="font-semibold text-slate-900">{userName}</span>
                              <div className="flex gap-1">
                                {[...Array(5)].map((_, i) => {
                                  const starClassName = i < rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300';
                                  return (
                                    <Star
                                      key={i}
                                      className={`w-4 h-4 ${starClassName}`}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                            <p className="text-slate-600">{comment}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}
          </div>

          {/* Instructor */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 border border-slate-100 sticky top-24">
              <h3 className="text-xl font-bold text-slate-900 mb-4">Your Instructor</h3>
              <div className="space-y-4">
                <img
                  src={course.instructor_image}
                  alt={course.instructor}
                  className="w-24 h-24 rounded-full object-cover"
                />
                <div>
                  <h4 className="text-lg font-semibold text-slate-900">{course.instructor}</h4>
                  <p className="text-sm text-slate-600 mt-2">{course.instructor_bio}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseDetailPage;
